import React, { useState } from "react";
import { useSnackbar } from "notistack";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  TextField,
  DialogActions,
  Button,
  LinearProgress,
  FormControl,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { getSnackbarOptions } from "../../../../../../../lib/getSnackbarOptions";
import { Category } from "../../../../../../../models/category/category";
import { CategoryAddDto } from "../../../../../../../models/category/categoryAddDto";
import { useApi } from "../../../../../../common/providers/Api.provider";
import { useTranslation } from "react-i18next";

export interface CategoryAddDialogComponentProps {
  questionnaireId: number;
  open: boolean;
  handleClose: (result?: Category) => void;
}

export function CategoryAddDialogComponent({
  questionnaireId,
  open,
  handleClose,
}: CategoryAddDialogComponentProps) {
  const { t: questionnaireTranslations } = useTranslation(
    "questionnaire-page",
    {
      keyPrefix: "categories.dialogs.add",
      useSuspense: false,
    }
  );
  const { t: commonTranslations } = useTranslation("common", {
    useSuspense: false,
  });

  const api = useApi();
  const snackbar = useSnackbar();

  const emptyState = {
    questionnaireId,
    name: "",
    scale: 0,
    showInResult: true,
  };

  const [isLoadingState, setIsLoadingState] = useState<boolean>(false);
  const [isSubmittedState, setIsSubmittedState] = useState<boolean>(false);
  const [fieldTouchedState, setFieldTouchedState] = useState<{
    name: boolean;
    scale: boolean;
  }>({
    name: false,
    scale: false,
  });

  const [categoryAddDtoState, setCategoryAddDtoState] =
    useState<CategoryAddDto>(emptyState);

  const isNameInvalid = !categoryAddDtoState.name.trim().length;
  const isScaleInvalid = categoryAddDtoState.scale <= 0;
  const showNameError = (isSubmittedState || fieldTouchedState.name) && isNameInvalid;
  const showScaleError =
    (isSubmittedState || fieldTouchedState.scale) && isScaleInvalid;

  const handleAdd = async (categoryAddDto: CategoryAddDto) => {
    setIsSubmittedState(true);

    if (
      !categoryAddDto.questionnaireId ||
      isNameInvalid ||
      categoryAddDto.scale <= 0
    ) {
      return;
    }

    try {
      setIsLoadingState(true);

      const response = await api.categoriesAdd(categoryAddDto);

      if (response.success) {
        snackbar.enqueueSnackbar(
          response.message,
          getSnackbarOptions({ variant: "success" })
        );
      } else {
        snackbar.enqueueSnackbar(
          response.message,
          getSnackbarOptions({ variant: "error" })
        );
      }

      handleClose(response.data);
    } catch (e) {
      snackbar.enqueueSnackbar(
        e as String,
        getSnackbarOptions({
          variant: "error",
        })
      );
    } finally {
      setCategoryAddDtoState(emptyState);
      setIsSubmittedState(false);
      setFieldTouchedState({
        name: false,
        scale: false,
      });
      setIsLoadingState(false);
    }
  };

  return (
    <Dialog open={open} className={"step-add-dialog-container"}>
      {isLoadingState && <LinearProgress />}
      <DialogTitle>{questionnaireTranslations("title")}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {questionnaireTranslations("description")}
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          required
          autoComplete="off"
          variant="outlined"
          margin="dense"
          id="name"
          label={questionnaireTranslations("name")}
          type="text"
          error={showNameError}
          helperText={
            showNameError ? questionnaireTranslations("nameRequired") : " "
          }
          value={categoryAddDtoState.name}
          onBlur={() =>
            setFieldTouchedState({
              ...fieldTouchedState,
              name: true,
            })
          }
          onChange={(event) =>
            setCategoryAddDtoState({
              ...categoryAddDtoState,
              name: event.target.value,
            })
          }
        />
        <TextField
          fullWidth
          required
          variant="outlined"
          margin="dense"
          id="description"
          label={questionnaireTranslations("scale")}
          type="number"
          error={showScaleError}
          helperText={
            showScaleError
              ? questionnaireTranslations("scaleGreaterThanZero")
              : " "
          }
          inputProps={{
            min: 1,
          }}
          value={categoryAddDtoState.scale}
          onBlur={() =>
            setFieldTouchedState({
              ...fieldTouchedState,
              scale: true,
            })
          }
          onChange={(event) =>
            setCategoryAddDtoState({
              ...categoryAddDtoState,
              scale: Number(event.target.value),
            })
          }
        />
        <FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={categoryAddDtoState.showInResult}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setCategoryAddDtoState({
                    ...categoryAddDtoState,
                    showInResult: event.target.checked,
                  });
                }}
              />
            }
            label={questionnaireTranslations("showInScanResult")}
          />
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleClose(undefined)} color="secondary">
          {commonTranslations("cancel")}
        </Button>
        <Button onClick={() => handleAdd(categoryAddDtoState)} color="primary">
          {commonTranslations("add")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
